import { useMemo, useState } from "react";
import { Link } from "wouter";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ToggleGroup,
  ToggleGroupCount,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import { Icon } from "@/components/ds/Icon";
import { ArrowRight } from "@/components/ds/icons";
import { useNovedadesContext } from "@/hooks/useNovedades";
import { whatsappSoporte } from "@/lib/support";
import { APP_VERSION } from "@/lib/version";
import { cn } from "@/lib/utils";
import {
  CATEGORIA_NOVEDAD_META,
  type CategoriaNovedad,
  type Novedad,
} from "@/types/novedad";

/** `todas` no es una categoría: es "sin filtro". */
type Filtro = "todas" | CategoriaNovedad;

const FILTROS: { valor: Filtro; label: string }[] = [
  { valor: "todas", label: "Todas" },
  { valor: "nueva-funcionalidad", label: "Nuevas funcionalidades" },
  { valor: "mejora", label: "Mejoras" },
  { valor: "correccion", label: "Correcciones" },
];

/**
 * "28 jul 2026". Las novedades vienen en fecha pura (sin hora): se leen en UTC
 * para que el día no se corra un paso hacia atrás en Chile.
 */
function formatFecha(fecha: string): string {
  return new Date(fecha).toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * "julio 2026" — el timeline agrupa por mes, no por día: hay varias novedades
 * el mismo día y un encabezado por fecha ensucia la lectura. Las mayúsculas
 * las pone el CSS del encabezado, no el string.
 */
function etiquetaMes(fecha: string): string {
  const d = new Date(fecha);
  const mes = d.toLocaleDateString("es-CL", { month: "long", timeZone: "UTC" });
  return `${mes} ${d.getUTCFullYear()}`;
}

interface GrupoMes {
  clave: string;
  label: string;
  filas: Novedad[];
}

/** Agrupa preservando el orden del feed (la API ya lo entrega descendente). */
function agruparPorMes(novedades: Novedad[]): GrupoMes[] {
  const grupos: GrupoMes[] = [];
  for (const n of novedades) {
    const clave = n.fecha.slice(0, 7);
    const ultimo = grupos[grupos.length - 1];
    if (ultimo?.clave === clave) ultimo.filas.push(n);
    else grupos.push({ clave, label: etiquetaMes(n.fecha), filas: [n] });
  }
  return grupos;
}

export function Novedades() {
  const { novedades, loading, error, sinLeer, hasUnseen, marcarVisto } =
    useNovedadesContext();
  const [filtro, setFiltro] = useState<Filtro>("todas");

  /**
   * Ids sin leer: las `sinLeer` primeras del feed COMPLETO. El filtro cambia
   * qué se ve, nunca qué se considera nuevo.
   */
  const idsSinLeer = useMemo(
    () => new Set(novedades.slice(0, sinLeer).map((n) => n.id)),
    [novedades, sinLeer],
  );

  /**
   * Contadores del segmented: sobre el total, no sobre lo filtrado. Por eso no
   * cambian al elegir un filtro — sirven justamente para decidir cuál elegir.
   */
  const conteos = useMemo(() => {
    const base: Record<Filtro, number> = {
      todas: novedades.length,
      "nueva-funcionalidad": 0,
      mejora: 0,
      correccion: 0,
    };
    for (const n of novedades) base[n.categoria] += 1;
    return base;
  }, [novedades]);

  const visibles = useMemo(
    () =>
      filtro === "todas" ? novedades : novedades.filter((n) => n.categoria === filtro),
    [novedades, filtro],
  );

  const grupos = useMemo(() => agruparPorMes(visibles), [visibles]);

  // El destacado es SIEMPRE el primero del feed (que viene ordenado por fecha
  // descendente): no es curaduría manual, y no lo cambia el filtro activo.
  const destacada = novedades[0];

  const handleMarcar = async () => {
    await marcarVisto();
    toast.success("Listo: marcamos todas las novedades como leídas.");
  };

  return (
    // 940px y no los 1360 del lienzo: esto es texto para leer, no una tabla.
    <div className="flex max-w-[940px] flex-col gap-[18px]">
      <PageHeader
        title="Novedades"
        subtitle="Lo que cambió en Facil Control, de lo más reciente a lo más antiguo."
        rightContent={
          <div className="flex flex-none items-center gap-2.5">
            <span className="fc-num inline-flex h-[31px] items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 text-xs text-muted-foreground">
              <Icon name="bolt" size={15} className="text-soft" />
              {APP_VERSION}
            </span>
            {hasUnseen && (
              <Button variant="outline" size="xs" onClick={handleMarcar}>
                <Icon name="done_all" size={16} className="text-muted-foreground" />
                Marcar como leídas
              </Button>
            )}
          </div>
        }
      />

      {loading ? (
        <CargandoNovedades />
      ) : error ? (
        <EstadoSimple
          icono="cloud_off"
          texto="No pudimos cargar las novedades. Intenta nuevamente más tarde."
        />
      ) : novedades.length === 0 ? (
        <EstadoSimple icono="campaign" texto="No hay novedades por ahora." />
      ) : (
        <>
          {destacada && <Destacada novedad={destacada} />}

          <div className="flex flex-wrap items-center gap-3">
            <ToggleGroup
              type="single"
              variant="segmented"
              value={filtro}
              // Radix permite deseleccionar el ítem activo y devuelve "". Acá no
              // existe el estado "sin filtro elegido": ignoramos el vacío.
              onValueChange={(v) => v && setFiltro(v as Filtro)}
              aria-label="Filtrar novedades por tipo"
              className="flex-wrap"
            >
              {FILTROS.map(({ valor, label }) => (
                <ToggleGroupItem key={valor} value={valor} aria-label={label}>
                  {label}
                  <ToggleGroupCount>{conteos[valor]}</ToggleGroupCount>
                </ToggleGroupItem>
              ))}
            </ToggleGroup>

            <div className="flex-1" />

            {hasUnseen && (
              <Badge variant="brand" dot className="h-[26px] rounded-full px-2.5">
                {sinLeer} {sinLeer === 1 ? "novedad nueva" : "novedades nuevas"} desde tu
                última visita
              </Badge>
            )}
          </div>

          {grupos.length === 0 ? (
            <EstadoSimple
              icono="filter_alt_off"
              texto="No hay novedades de este tipo todavía."
            />
          ) : (
            grupos.map((grupo) => (
              <section key={grupo.clave} className="flex flex-col gap-2.5">
                <div className="flex items-center gap-2.5">
                  <h2 className="fc-th">{grupo.label}</h2>
                  {/* Cuenta lo VISIBLE con el filtro activo, no el total del mes. */}
                  <span className="fc-num text-[11px] text-soft">{grupo.filas.length}</span>
                  {/* `bg-input` (#E4E4E7) y no `bg-border` (#ECECF0): esta línea
                      va sobre el LIENZO (#F2F4F6), no sobre una tarjeta blanca.
                      Con el borde de tarjeta desaparece — mismo gotcha que un
                      contenedor con borde y sin `bg-card`. */}
                  <div className="h-px flex-1 bg-input" />
                </div>
                <div className="flex flex-col">
                  {grupo.filas.map((novedad, i) => (
                    <FilaNovedad
                      key={novedad.id}
                      novedad={novedad}
                      sinLeer={idsSinLeer.has(novedad.id)}
                      conector={i < grupo.filas.length - 1}
                    />
                  ))}
                </div>
              </section>
            ))
          )}

          <Cierre />
        </>
      )}
    </div>
  );
}

/**
 * Bloque destacado. Lleva el ÚNICO botón teal de la pantalla — la regla de un
 * primario por pantalla se cumple porque el encabezado no tiene CTA propio.
 */
function Destacada({ novedad }: { novedad: Novedad }) {
  const meta = CATEGORIA_NOVEDAD_META[novedad.categoria];

  return (
    <div className="flex flex-col gap-[11px] rounded-2xl border border-primary-200 bg-primary-50 px-5 py-[18px]">
      <div className="flex flex-wrap items-center gap-2.5">
        <MarcaTeal icono="campaign">LO ÚLTIMO</MarcaTeal>
        <Badge variant={meta.tono}>
          <Icon name={meta.icono} size={14} />
          {meta.label}
        </Badge>
        <span className="fc-num text-[11.5px] text-primary-hover">
          {formatFecha(novedad.fecha)}
        </span>
      </div>
      <h2 className="text-[17px] leading-[1.3] font-semibold tracking-[-0.015em] text-pretty text-primary-strong">
        {novedad.titulo}
      </h2>
      <p className="max-w-[720px] text-[13px] leading-relaxed text-pretty text-body">
        {novedad.descripcion}
      </p>
      {novedad.ruta && novedad.cta && (
        // `self-start`: el ancho lo fija el texto del botón, no la columna. Su
        // alto es fijo y no perdona un salto de línea — de ahí también el
        // `whitespace-nowrap` que ya trae el botón del DS.
        <Button asChild size="xs" className="max-w-full self-start">
          <Link href={novedad.ruta}>
            {novedad.cta}
            <ArrowRight className="size-4" />
          </Link>
        </Button>
      )}
    </div>
  );
}

/** Una fila del timeline: columna del punto + tarjeta. */
function FilaNovedad({
  novedad,
  sinLeer,
  conector,
}: {
  novedad: Novedad;
  sinLeer: boolean;
  conector: boolean;
}) {
  const meta = CATEGORIA_NOVEDAD_META[novedad.categoria];

  return (
    // `items-stretch` es lo que deja al conector llegar hasta la fila siguiente:
    // la altura la fija la tarjeta, con su `mb-3` incluido.
    <div className="flex items-stretch gap-[13px]">
      <div className="flex w-[30px] flex-none flex-col items-center">
        <div
          className={cn(
            "flex size-[30px] flex-none items-center justify-center rounded-full",
            meta.circuloClassName,
          )}
        >
          <Icon name={meta.icono} size={17} />
        </div>
        {/* Mismo motivo que el divisor del grupo: el conector va sobre el
            lienzo. Con `bg-border-subtle` (#F1F1F4) queda literalmente
            invisible contra el #F2F4F6 del fondo. */}
        {conector && <div className="my-[5px] w-0.5 flex-1 bg-input" />}
      </div>

      <article
        className={cn(
          "mb-3 flex min-w-0 flex-1 flex-col gap-[7px] rounded-2xl border px-[15px] py-[13px] transition-shadow hover:shadow-[var(--shadow-card-hover)]",
          sinLeer ? "border-primary-200 bg-primary-50" : "border-border bg-card",
        )}
      >
        <div className="flex flex-wrap items-start gap-2.5">
          {/* `min-w-[200px]` hace que el badge baje a la línea siguiente en vez
              de estrujar el título hasta partirlo palabra por palabra. */}
          <h3 className="min-w-[200px] flex-1 text-sm leading-[1.35] font-semibold tracking-[-0.01em] text-pretty">
            {novedad.titulo}
          </h3>
          <div className="flex flex-none items-center gap-1.5">
            {sinLeer && <MarcaTeal>NUEVO</MarcaTeal>}
            <Badge variant={meta.tono}>{meta.label}</Badge>
          </div>
        </div>

        <p className="text-[12.5px] leading-relaxed text-pretty text-body">
          {novedad.descripcion}
        </p>

        <div className="flex flex-wrap items-center gap-2.5">
          <span className="fc-num text-[11.5px] text-soft">{formatFecha(novedad.fecha)}</span>
          {novedad.ruta && novedad.cta && (
            <Button
              asChild
              variant="outline"
              size="xs"
              className="border-border text-primary-hover hover:border-primary-border hover:bg-primary-50"
            >
              <Link href={novedad.ruta}>
                {novedad.cta}
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          )}
        </div>
      </article>
    </div>
  );
}

/**
 * Marca de novedad — "LO ÚLTIMO" y "NUEVO".
 *
 * Teal sólido con texto blanco, y NO usa `<Badge>` a propósito: el badge del DS
 * es de ESTADO (fondo suave + texto saturado, nunca blanco sobre color) y estas
 * dos no describen el estado de un dato, marcan qué mirar primero. Están en el
 * mismo registro que el botón primario, y por eso comparten su teal.
 */
function MarcaTeal({ icono, children }: { icono?: string; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex flex-none items-center gap-1.5 bg-primary font-semibold tracking-[0.04em] text-primary-foreground",
        icono
          ? "h-[22px] rounded-sm px-2.5 text-[11px]"
          : "h-5 rounded-xs px-[7px] text-[10px]",
      )}
    >
      {icono && <Icon name={icono} size={14} />}
      {children}
    </span>
  );
}

/** Cierre de la pantalla: sin esto el timeline termina en el vacío. */
function Cierre() {
  return (
    <div className="flex flex-wrap items-center gap-[11px] rounded-2xl border border-border bg-surface-muted px-[15px] py-[13px]">
      <Icon name="forum" size={19} className="text-soft" />
      <p className="min-w-[200px] flex-1 text-[12.5px] leading-normal text-body">
        Estás al día con la versión{" "}
        <strong className="fc-num text-xs font-medium">{APP_VERSION}</strong>. ¿Te falta
        algo para trabajar más tranquilo?
      </p>
      <Button asChild variant="outline" size="xs" className="flex-none">
        <a
          href={whatsappSoporte("Hola, quiero sugerir una mejora para Fácil Control.")}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon name="lightbulb" size={16} className="text-muted-foreground" />
          Sugerir una mejora
        </a>
      </Button>
    </div>
  );
}

/** Estado vacío / de error del DS: ícono de 26px y una línea que diga qué pasa. */
function EstadoSimple({ icono, texto }: { icono: string; texto: string }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-card px-5 py-10 text-center">
      <Icon name={icono} size={26} className="text-icon-soft" />
      <span className="text-[13.5px] text-muted-foreground">{texto}</span>
    </div>
  );
}

function CargandoNovedades() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-[150px] rounded-2xl" />
      <Skeleton className="h-[28px] w-[380px] max-w-full rounded-[9px]" />
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-stretch gap-[13px]">
          <Skeleton className="size-[30px] flex-none rounded-full" />
          <Skeleton className="h-[104px] flex-1 rounded-2xl" />
        </div>
      ))}
    </div>
  );
}
